import { useEffect, useRef, useState } from "react";

export default function useAutoLogout(
  onLogout,
  { inactivityLimit = 10 * 60 * 1000, warningThreshold = 60 * 1000 } = {}
) {
  const [timeLeft, setTimeLeft] = useState(inactivityLimit);
  const [isWarning, setIsWarning] = useState(false);

  const warningRef = useRef(false);
  const logoutRef = useRef(onLogout);
  const LAST_ACTIVE_KEY = "lastActivity";

  // keep onLogout always updated (without retriggering effects)
  useEffect(() => {
    logoutRef.current = onLogout;
  }, [onLogout]);

  // keep ref in sync with state
  useEffect(() => {
    warningRef.current = isWarning;
  }, [isWarning]);

  // 🔹 Run inactivity check every second (runs ONCE)
  useEffect(() => {
    const checkInactivity = () => {
      const lastActiveValue = localStorage.getItem(LAST_ACTIVE_KEY);

      if (!lastActiveValue) {
        localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
        setTimeLeft(inactivityLimit);
        return false;
      }

      const lastActive = parseInt(lastActiveValue, 10);
      const diff = Date.now() - lastActive;

      const remaining = Math.max(inactivityLimit - diff, 0);
      setTimeLeft(remaining);

      if (remaining === 0) {
        logoutRef.current?.();
        console.log("Session expired (inactivity checker)");
        return true;
      }

      return false;
    };

    // first check on mount
    if (checkInactivity()) return;

    const interval = setInterval(checkInactivity, 1000);
    return () => clearInterval(interval);
  }, [inactivityLimit]); // DOES NOT depend on onLogout now

  // 🔹 Activity listeners (runs once)
  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "touchstart"];

    const handleActivity = () => {
      if (!warningRef.current) {
        const now = Date.now();
        localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
        setTimeLeft(inactivityLimit);
      }
    };

    events.forEach((e) => window.addEventListener(e, handleActivity));

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1000, 0));
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearInterval(interval);
    };
  }, [inactivityLimit]);

  // warning visibility
  useEffect(() => {
    const shouldShow = timeLeft > 0 && timeLeft <= warningThreshold;
    setIsWarning(shouldShow);
  }, [timeLeft, warningThreshold]);

  // logout when countdown hits 0
  useEffect(() => {
    if (timeLeft === 0) {
      logoutRef.current?.();
      console.log("Session expired (countdown watcher)");
    }
  }, [timeLeft]);

  // stay connected
  const stayConnected = () => {
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
    setTimeLeft(inactivityLimit);
    setIsWarning(false);
    warningRef.current = false;
  };

  // initialize last activity on first mount
  useEffect(() => {
    if (!localStorage.getItem(LAST_ACTIVE_KEY)) {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    }
  }, []);

  return { timeLeft, isWarning, stayConnected };
}
